import { BUYER_STAGE_LABELS, BUYER_STAGE_ORDER } from './reportKpis.js';
/** A short line that names a stage, or null. */
function stageNamed(text) {
    const t = text
        .toLowerCase()
        .replace(/[^a-z0-9 ]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    for (const stage of BUYER_STAGE_ORDER) {
        if (t.includes(BUYER_STAGE_LABELS[stage].toLowerCase()))
            return stage;
    }
    const n = /^stage ([1-5])\b/.exec(t);
    return n ? BUYER_STAGE_ORDER[Number(n[1]) - 1] : null;
}
/**
 * A heading is (almost) only the stage name: "Stage 2", "Workflow Evaluation",
 * "Stage 5 – Supplier Selection (5 questions):". A short QUESTION that merely
 * mentions a stage ("Which supplier selection criteria matter?") is not.
 */
function isHeading(line, stage) {
    if (line.trim().endsWith('?'))
        return false;
    const rest = line
        .toLowerCase()
        .replace(BUYER_STAGE_LABELS[stage].toLowerCase(), ' ')
        .replace(/\bstage\s*[1-5]\b/g, ' ')
        .replace(/\b\d+\s*(questions?|prompts?)\b/g, ' ')
        .replace(/[^a-z]+/g, ' ')
        .trim();
    return rest === '' || rest.split(' ').length <= 2;
}
const NUMBERING = /^\s*(?:(?:p|q|prompt|question)?\s*\d{1,2}\s*[.):\]-]|[-*•–])\s+/i;
/** A question is a real sentence; shorter cells are labels, ids or headers. */
const MIN_QUESTION_CHARS = 20;
export function parseImportedQuestions(text, perStage = 5) {
    const rows = [];
    let heading = null;
    let sawStage = false;
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (!line)
            continue;
        if (line.includes('\t')) {
            const cells = line.split('\t').map((c) => c.trim()).filter(Boolean);
            const longest = [...cells].sort((a, b) => b.length - a.length)[0] ?? '';
            if (longest.length < MIN_QUESTION_CHARS)
                continue; // header / label row
            const stageCell = cells.find((c) => c !== longest && c.length < 60 && stageNamed(c));
            const stage = stageCell ? stageNamed(stageCell) : heading;
            // A merged stage cell copies onto its first row only: carry it down.
            if (stageCell) {
                sawStage = true;
                heading = stage;
            }
            rows.push({ stage, prompt: longest.replace(NUMBERING, '').trim() });
            continue;
        }
        const named = line.length < 80 ? stageNamed(line) : null;
        if (named && isHeading(line, named)) {
            heading = named;
            sawStage = true;
            continue;
        }
        const prompt = line.replace(NUMBERING, '').trim();
        if (prompt.length < MIN_QUESTION_CHARS)
            continue;
        rows.push({ stage: heading, prompt });
    }
    const problems = [];
    const total = perStage * BUYER_STAGE_ORDER.length;
    let stagesFrom = 'none';
    let questions = [];
    if (rows.length === 0) {
        problems.push('No questions found in the pasted text.');
    }
    else if (sawStage) {
        stagesFrom = 'headings';
        const unplaced = rows.filter((r) => r.stage === null).length;
        if (unplaced > 0) {
            problems.push(`${unplaced} question(s) come before the first stage heading, so their stage is unknown.`);
        }
        questions = rows
            .filter((r) => r.stage !== null)
            .sort((a, b) => BUYER_STAGE_ORDER.indexOf(a.stage) - BUYER_STAGE_ORDER.indexOf(b.stage));
    }
    else {
        stagesFrom = 'order';
        if (rows.length !== total) {
            problems.push(`Found ${rows.length} questions. Without stage headings the paste must hold exactly ${total}, in stage order (${perStage} per stage).`);
        }
        questions = rows.map((r, i) => ({
            stage: BUYER_STAGE_ORDER[Math.min(Math.floor(i / perStage), BUYER_STAGE_ORDER.length - 1)],
            prompt: r.prompt,
        }));
    }
    if (questions.length > 0) {
        for (const stage of BUYER_STAGE_ORDER) {
            const n = questions.filter((q) => q.stage === stage).length;
            if (n !== perStage) {
                problems.push(`${BUYER_STAGE_LABELS[stage]} has ${n} question(s); it needs exactly ${perStage}.`);
            }
        }
        const seen = new Set();
        for (const q of questions) {
            const k = q.prompt.toLowerCase();
            if (seen.has(k))
                problems.push(`Duplicate question: "${q.prompt.slice(0, 80)}"`);
            seen.add(k);
        }
        const tooLong = questions.filter((q) => q.prompt.length > 2000).length;
        if (tooLong > 0)
            problems.push(`${tooLong} question(s) are longer than 2,000 characters.`);
    }
    return { questions, problems, stagesFrom };
}
//# sourceMappingURL=questionImport.js.map